import { Button } from "../../../../shadcn/ui/button";
import { Input } from "../../../../shadcn/ui/input";
import { Label } from "../../../../shadcn/ui/label";
import { Switch } from "../../../../shadcn/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../shadcn/ui/popover";
import { Calendar as CalendarComponent } from "../../../../shadcn/ui/calendar";
import { ArrowLeft, Calendar, MapPin, Tag, HelpCircle } from "lucide-react";
import { useHistory } from "react-router-dom";
import type { ListingData } from "./index";
import { cn } from "../../../../lib/utils";
import { useEffect, useState } from "react";
import billingService from "../../../../services/billingService";
import InstanceDetailsCard from "./InstanceDetailsCard";
import ValidationMessage from "./ValidationMessage";
import SaveStatusIndicator from "./SaveStatusIndicator";
import { RotateCcw } from "lucide-react";
import { format } from "date-fns/format";
import logger from "../../../../utils/logger";


interface StepOneProps {
  data: ListingData;
  updateData: (data: Partial<ListingData>) => void;
  onNext: () => void;
  lastSaved: Date | null;
  onClearForm: () => void;
  isEditMode?: boolean;
}

export default function StepOne({ data, updateData, onNext, lastSaved, onClearForm, isEditMode }: StepOneProps) {
  const history = useHistory();
  const [subcategories, setSubcategories] = useState<Array<{ id: number; name: string; description?: string }>>([]);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push("/host/spaces?type=play");
    }
  };
  const [loadingSubcategories, setLoadingSubcategories] = useState<boolean>(false);

  // Load PLAY subcategories (categoryId = 3)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingSubcategories(true);
        const resp = await billingService.getServiceSubcategories(3);
        const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp?.data?.data) ? resp.data.data : [];
        if (mounted) {
          setSubcategories(list.map((s: any) => ({ id: s.id, name: s.name, description: s.description })));
        }
      } catch (e) {
        if (mounted) setSubcategories([]);
      } finally {
        if (mounted) setLoadingSubcategories(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Validation for required fields - Subcategory and Floor
  const isValid = !!data.subCategoryId && !!data.floorId;

  const getValidationMessage = () => {
    if (!data.subCategoryId) return "Please select a category";
    if (!data.floorId) return "Please select a floor";
    return null;
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b bg-background">
        <div className="mx-auto max-w-4xl px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">Step 1 of 4</span>
              <SaveStatusIndicator lastSaved={lastSaved} />
              {!isEditMode && (
                <Button variant="ghost" size="sm" onClick={onClearForm} className="gap-2" {...({} as any)}>
                  <RotateCcw className="h-4 w-4" />
                  Start Over
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="border-b bg-background">
        <div className="mx-auto max-w-4xl px-6 py-6">
          <h1 className="mb-2 text-2xl font-semibold">
            {isEditMode ? "Edit Play Service" : "Create New Play Service"}
          </h1>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full w-1/4 bg-foreground transition-all" />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Basic Information</span>
            <span className="text-muted-foreground">25% Complete</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-2xl px-6 py-12">
        <div className="mb-12 text-center">
          <h2 className="mb-2 text-3xl font-semibold">
            {isEditMode ? "Update the basics" : "Let\'s start with the basics"}
          </h2>
          <p className="text-muted-foreground">Tell us about your experience so participants know what to expect</p>
        </div>

        <div className="space-y-8">
          {/* Removed Experience Title - not used by payload */}

          {/* Category (Play Subcategories) */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Tag className="h-5 w-5" />
              <h3 className="font-semibold">Category</h3>
            </div>
            <div className="space-y-3">
              <Label {...({} as any)}>Select a subcategory</Label>
              {loadingSubcategories ? (
                <div className="text-sm text-muted-foreground">Loading categories...</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {subcategories.map(sc => (
                    <button
                      key={sc.id}
                      onClick={() => updateData({ subCategoryId: sc.id })}
                      className={cn(
                        "rounded-lg border-2 p-4 text-left transition-all hover:border-foreground",
                        data.subCategoryId === sc.id ? "border-foreground bg-accent" : "border-border bg-background"
                      )}
                    >
                      <div className="font-semibold">{sc.name}</div>
                      {sc.description && <div className="text-sm text-muted-foreground">{sc.description}</div>}
                    </button>
                  ))}
                  {subcategories.length === 0 && (
                    <div className="text-sm text-muted-foreground">No categories available</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Date & Time */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              <h3 className="font-semibold">Date & Time</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="datetime" {...({} as any)}>
                  When will this experience take place?
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !data.dateTime && "text-muted-foreground"
                      )}
                      {...({} as any)}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {data.dateTime ? format(new Date(data.dateTime), "PPP 'at' p") : "Pick a date and time"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" {...({} as any)}>
                    <CalendarComponent
                      mode="single"
                      selected={data.dateTime ? new Date(data.dateTime) : undefined}
                      onSelect={(date: Date | undefined) => {
                        if (date) {
                          const timeStr = data.dateTime ? data.dateTime.split("T")[1] || "09:00" : "09:00";
                          updateData({ dateTime: `${format(date, "yyyy-MM-dd")}T${timeStr}` });
                        }
                      }}
                      disabled={(date: Date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      className={undefined}
                      classNames={undefined}
                      components={undefined}
                      {...({} as any)}
                    />
                    <div className="p-3 border-t">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Label className="text-xs mb-2 block" {...({} as any)}>
                            Start Time
                          </Label>
                          <Input
                            type="time"
                            value={data.dateTime ? data.dateTime.split("T")[1] || "09:00" : "09:00"}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const dateStr = data.dateTime
                                ? data.dateTime.split("T")[0]
                                : format(new Date(), "yyyy-MM-dd");
                              updateData({ dateTime: `${dateStr}T${e.target.value}` });
                            }}
                            {...({} as any)}
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs mb-2 block" {...({} as any)}>
                            End Time
                          </Label>
                          <Input
                            type="time"
                            value={data.endTime || "17:00"}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              updateData({ endTime: e.target.value });
                            }}
                            {...({} as any)}
                          />
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="recurring"
                  checked={data.isRecurring}
                  onCheckedChange={(checked: boolean) => updateData({ isRecurring: checked })}
                  {...({} as any)}
                />
                <div>
                  <Label htmlFor="recurring" className="cursor-pointer font-semibold" {...({} as any)}>
                    This is a recurring experience
                  </Label>
                  <p className="text-sm text-muted-foreground">Participants can book multiple sessions</p>
                </div>
              </div>
              {data.isRecurring && (
                <div className="space-y-2">
                  <Label htmlFor="recurrenceEnd" {...({} as any)}>
                    Recurrence End Date
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !data.recurrenceEndDate && "text-muted-foreground"
                        )}
                        {...({} as any)}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {data.recurrenceEndDate ? format(new Date(data.recurrenceEndDate), "PPP") : "Pick end date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" {...({} as any)}>
                      <CalendarComponent
                        mode="single"
                        selected={data.recurrenceEndDate ? new Date(data.recurrenceEndDate) : undefined}
                        onSelect={(date: Date | undefined) =>
                          date && updateData({ recurrenceEndDate: format(date, "yyyy-MM-dd") })
                        }
                        disabled={(date: Date) => {
                          const today = new Date(new Date().setHours(0, 0, 0, 0));
                          const startDate = data.dateTime ? new Date(data.dateTime.split("T")[0]) : today;
                          return date < today || date < startDate;
                        }}
                        initialFocus
                        className={undefined}
                        classNames={undefined}
                        components={undefined}
                        {...({} as any)}
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">Required by the schedule when recurring is enabled</p>
                </div>
              )}
            </div>
          </div>

          {/* Instance Details - Groups all instance-related fields together */}
          <InstanceDetailsCard data={data} updateData={updateData} />

          {/* Presented By (Display Name & Image) */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Tag className="h-5 w-5" />
              <h3 className="font-semibold">Presented By</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hostDisplayName" {...({} as any)}>
                  Display Name
                </Label>
                <Input
                  id="hostDisplayName"
                  placeholder="e.g., Acme Corp Workspaces"
                  value={data.hostDisplayName || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateData({ hostDisplayName: e.target.value })
                  }
                  {...({} as any)}
                />
              </div>

              <div className="space-y-2">
                <Label {...({} as any)}>Display Image</Label>
                <div className="flex flex-col gap-4 items-start">
                  <label className="flex relative justify-center items-center w-20 h-20 text-gray-400 bg-gray-100 rounded-lg border-2 border-gray-300 border-dashed transition cursor-pointer hover:border-blue-400">
                    {data.hostDisplayImage ? (
                      <img
                        src={data.hostDisplayImage}
                        alt="Display"
                        className="object-cover w-20 h-20 rounded-lg"
                      />
                    ) : (
                      <Tag className="w-8 h-8" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const formData = new FormData();
                          formData.append("file", file);
                          const spaceMgmt = await import("../../../../services/host-apiServices/spaceManagementService");
                          const res = await spaceMgmt.default.uploadMedia([file]);
                          const url = Array.isArray(res.data) ? res.data[0] : (res.data as any);
                          if (url && typeof url === "string") {
                            updateData({ hostDisplayImage: url });
                          }
                        } catch (err) {
                          logger.error("Failed to upload display image", err);
                        }
                      }}
                    />
                  </label>
                  {data.hostDisplayImage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => updateData({ hostDisplayImage: "" })}
                      {...({} as any)}
                    >
                      Remove Image
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Duration Settings */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              <h3 className="font-semibold">Duration Settings</h3>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minDuration" {...({} as any)}>
                    Minimum Duration (hours)
                  </Label>
                  <Input
                    id="minDuration"
                    type="number"
                    min="1"
                    value={data.minimumDuration}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateData({ minimumDuration: parseInt(e.target.value) || 1 })
                    }
                    {...({} as any)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxDuration" {...({} as any)}>
                    Maximum Duration (hours)
                  </Label>
                  <Input
                    id="maxDuration"
                    type="number"
                    min="1"
                    value={data.maximumDuration}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateData({ maximumDuration: parseInt(e.target.value) || 8 })
                    }
                    {...({} as any)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bufferTime" className="flex items-center" {...({} as any)}>
                  Buffer Time <HelpCircle className="w-4 h-4 ml-1 text-muted-foreground" />
                </Label>
                <Select
                  value={String(data.bufferTimeInMinutes)}
                  onValueChange={value => updateData({ bufferTimeInMinutes: parseInt(value) })}
                >
                  <SelectTrigger id="bufferTime" {...({} as any)}>
                    <SelectValue placeholder="Select buffer time" {...({} as any)} />
                  </SelectTrigger>
                  <SelectContent {...({} as any)}>
                    <SelectItem value="0" {...({} as any)}>
                      No buffer
                    </SelectItem>
                    <SelectItem value="15" {...({} as any)}>
                      15 Minutes
                    </SelectItem>
                    <SelectItem value="30" {...({} as any)}>
                      30 Minutes
                    </SelectItem>
                    <SelectItem value="60" {...({} as any)}>
                      1 Hour
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Time between bookings for cleaning and setup</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8">
          <div className="mb-4">
            {(() => {
              const showValidation = !isValid && (!isEditMode || !data.subCategoryId);
              return (
                <ValidationMessage message={showValidation && getValidationMessage() ? getValidationMessage()! : ""} />
              );
            })()}
          </div>
          <div className="flex justify-end">
            <Button onClick={onNext} disabled={!isValid} size="lg" className="gap-2" {...({} as any)}>
              Continue to Details
              <span>→</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
